// Name matching for duplicate detection (spec §112): Greek ⇄ Latin transliteration and common
// first-name variants (Giannis = Ioannis = Yiannis = Γιάννης = John). Matching never merges by
// itself; it only decides whether two records deserve a side-by-side comparison.

const GREEK: Record<string, string> = {
  α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 'th', ι: 'i', κ: 'k', λ: 'l', μ: 'm', ν: 'n', ξ: 'x', ο: 'o', π: 'p', ρ: 'r', σ: 's', ς: 's', τ: 't', υ: 'y', φ: 'f', χ: 'ch', ψ: 'ps', ω: 'o',
};
const DIGRAPHS: [RegExp, string][] = [[/ου/g, 'ou'], [/αυ/g, 'av'], [/ευ/g, 'ev'], [/αι/g, 'ai'], [/ει/g, 'ei'], [/οι/g, 'oi'], [/μπ/g, 'b'], [/ντ/g, 'd'], [/γγ/g, 'ng'], [/γκ/g, 'g'], [/τσ/g, 'ts'], [/τζ/g, 'tz']];
/** accent-free lowercase Latin form */
export function latinize(s: string): string {
  let x = String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  for (const [re, r] of DIGRAPHS) x = x.replace(re, r);
  x = x.replace(/[α-ω]/g, (c) => GREEK[c] ?? c);
  return x.replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}
/** phonetic squeeze so Latin spellings of the same Greek name meet (y/i, ph/f, ch/h, k/c, double letters) */
function squeeze(s: string): string {
  return s.replace(/ph/g, 'f').replace(/th/g, 't').replace(/ch/g, 'h').replace(/[yj]/g, 'i').replace(/c/g, 'k').replace(/ou/g, 'u').replace(/ei|oi|ai/g, (m) => (m === 'ai' ? 'e' : 'i')).replace(/(.)\1+/g, '$1');
}

/** canonical first names: every variant maps to the first entry */
const VARIANTS: string[][] = [
  ['ioannis', 'giannis', 'yiannis', 'yannis', 'gianis', 'ianis', 'john', 'jon', 'johnny', 'giannakis'],
  ['georgios', 'giorgos', 'yiorgos', 'yorgos', 'george', 'jorge'],
  ['konstantinos', 'kostas', 'costas', 'kostis', 'constantine', 'dinos', 'ntinos'],
  ['nikolaos', 'nikos', 'nicholas', 'nicolas', 'nick', 'nicos'],
  ['andreas', 'antreas', 'andrew', 'andy'],
  ['christos', 'chris', 'christakis'],
  ['panagiotis', 'panayiotis', 'panos', 'takis', 'panikos'],
  ['dimitrios', 'dimitris', 'demetris', 'demetrios', 'jim', 'mimis'],
  ['michail', 'michalis', 'michael', 'mike', 'mihalis'],
  ['charalambos', 'charalampos', 'haralambos', 'harris', 'babis', 'bambos', 'lambros'],
  ['stylianos', 'stelios', 'stelianos', 'steve'],
  ['vasileios', 'vasilis', 'vassilis', 'basil', 'bill', 'vasos'],
  ['antonios', 'antonis', 'anthony', 'tony', 'andonis'],
  ['petros', 'peter'], ['pavlos', 'paul'], ['sotirios', 'sotiris'], ['emmanouil', 'manolis', 'emmanuel', 'manos'],
  ['athanasios', 'thanasis', 'thanos', 'sakis'], ['evangelos', 'vangelis', 'evangelos', 'angelos'], ['spyridon', 'spyros', 'spiros'],
  ['alexandros', 'alexis', 'alex', 'alexander'], ['kyriakos', 'kyriacos', 'kiriakos'], ['savvas', 'savas'], ['marios', 'mario'],
  ['maria', 'mary', 'marie', 'marikaki'], ['eleni', 'helen', 'elena', 'lena', 'helena'], ['aikaterini', 'katerina', 'catherine', 'katie', 'kaiti'],
  ['anastasia', 'natasa', 'tasoula', 'sia'], ['georgia', 'gogo', 'giota'], ['panagiota', 'panayiota', 'giota', 'yiota'], ['christina', 'christiana', 'tina'],
  ['sofia', 'sophia', 'sophie'], ['ioanna', 'joanna', 'jo'], ['konstantina', 'dina', 'ntina', 'constantina'], ['evangelia', 'litsa', 'lia'], ['despoina', 'despina'],
];
const CANON = new Map<string, string>();
for (const group of VARIANTS) for (const n of group) CANON.set(squeeze(latinize(n)), group[0]!);

export function canonicalFirstName(name: string): string {
  const first = latinize(name).split(' ')[0] ?? '';
  const sq = squeeze(first);
  return CANON.get(sq) ?? sq;
}
/** canonical comparable form of a full name: canonical first name + squeezed remaining parts */
export function nameSignature(full: string): { first: string; last: string } {
  const parts = latinize(full).split(' ').filter(Boolean);
  return { first: canonicalFirstName(parts[0] ?? ''), last: squeeze(parts.slice(1).join(' ')) };
}
/** do two names plausibly denote the same person? (same canonical first name and same surname) */
export function sameName(a: string, b: string): boolean {
  const x = nameSignature(a), y = nameSignature(b);
  return !!x.first && x.first === y.first && x.last === y.last;
}
export function sameSurname(a: string, b: string): boolean {
  const x = nameSignature(a), y = nameSignature(b);
  return !!x.last && x.last === y.last;
}
